import {
  type AiUsageStats,
  type BillingStats,
  type BookingStats,
  type ChatStats,
  type CourseStats,
  type MatchingStats,
  type MemberStats,
  type ReportStats,
  type StatsRangeKey,
  type TrustStats,
  type VerificationStats,
  type ViolationStats,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The Dashboard's own read of every module's `GET .../stats` (D12) — one slice
 * per admin surface would be one round trip's worth of boilerplate ten times
 * over for a page that reads every one of them together, so the bridge lives
 * here instead. Each endpoint still calls the OWNING module's route; nothing
 * here computes a figure itself (U1 — this module counts nothing of its own).
 */
export const dashboardStatsApi = createApi({
  reducerPath: 'dashboardStatsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    memberStats: builder.query<MemberStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/users/stats', method: 'GET', params: { range } }),
    }),
    verificationStats: builder.query<VerificationStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/verification/stats', method: 'GET', params: { range } }),
    }),
    matchingStats: builder.query<MatchingStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/matching/stats', method: 'GET', params: { range } }),
    }),
    chatStats: builder.query<ChatStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/chat/stats', method: 'GET', params: { range } }),
    }),
    billingStats: builder.query<BillingStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/billing/stats', method: 'GET', params: { range } }),
    }),
    reportStats: builder.query<ReportStats, StatsRangeKey>({
      query: (range) => ({
        url: '/admin/moderation/reports/stats',
        method: 'GET',
        params: { range },
      }),
    }),
    violationStats: builder.query<ViolationStats, StatsRangeKey>({
      query: (range) => ({
        url: '/admin/moderation/violations/stats',
        method: 'GET',
        params: { range },
      }),
    }),
    trustStats: builder.query<TrustStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/trust/stats', method: 'GET', params: { range } }),
    }),
    courseStats: builder.query<CourseStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/courses/stats', method: 'GET', params: { range } }),
    }),
    bookingStats: builder.query<BookingStats, StatsRangeKey>({
      query: (range) => ({ url: '/admin/bookings/stats', method: 'GET', params: { range } }),
    }),
    /**
     * The one section not scoped to the shared range picker: the AI usage
     * dashboard already has its own window (`03-ai-platform.md`'s
     * month-to-date-vs-budget figure is deliberately window-independent), and
     * this is the first admin home for it (PROGRESS §Known issues, l.2566).
     */
    aiUsageStats: builder.query<AiUsageStats, void>({
      query: () => ({ url: '/admin/ai/usage/stats', method: 'GET' }),
    }),
  }),
});

export const {
  useMemberStatsQuery,
  useVerificationStatsQuery,
  useMatchingStatsQuery,
  useChatStatsQuery,
  useBillingStatsQuery,
  useReportStatsQuery,
  useViolationStatsQuery,
  useTrustStatsQuery,
  useCourseStatsQuery,
  useBookingStatsQuery,
  useAiUsageStatsQuery,
} = dashboardStatsApi;
